import type { TenderPosition } from './catalog-client';
import { formatBudget } from './format';

/**
 * «Что закупают» — number formatting for one line item (`CatalogItem.positions`).
 *
 * ⚠️ No `Intl` / `toLocaleString` here on purpose: Chrome ships no `uz` CLDR
 * data, so a quantity formatted for `uz` renders with en-style grouping in a
 * real browser while every Node test passes (see `formatBudgetCompact`). Both
 * site languages group thousands with a space and use a decimal comma, so one
 * hand-rolled rule is correct for ru, uz and kk alike — and identical in SSR,
 * prerender and the browser, so it can never cause a hydration mismatch.
 */

/** Rows shown before «Показать все N позиций». */
export const POSITIONS_PREVIEW = 8;

/** 1500 → «1 500», 2.5 → «2,5», 0.125 → «0,125» (at most three decimals). */
export function formatQty(n: number): string {
  if (!Number.isFinite(n)) return '';
  const rounded = Math.round(n * 1000) / 1000;
  const [int, frac] = rounded.toFixed(3).replace(/0+$/, '').replace(/\.$/, '').split('.');
  const grouped = (int ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return frac ? `${grouped},${frac}` : grouped;
}

/** The formatted numbers a position actually carries; null where it has none. */
export interface PositionFigures {
  /** «20 шт» — the quantity with its unit, or the bare quantity. */
  qty: string | null;
  /** «40 000 000» — unit price, so'm, without the currency word. */
  unitPrice: string | null;
  /** «800 000 000» — line total, so'm, without the currency word. */
  total: string | null;
}

export function positionFigures(p: TenderPosition): PositionFigures {
  const qty =
    typeof p.qty === 'number' && p.qty > 0
      ? `${formatQty(p.qty)}${p.unit ? ` ${p.unit}` : ''}`
      : null;
  const money = (v: number | undefined): string | null =>
    typeof v === 'number' && Number.isFinite(v) && v > 0
      ? formatBudget(String(Math.round(v)))
      : null;
  return { qty, unitPrice: money(p.unitPriceUzs), total: money(p.totalUzs) };
}

/** How many positions the source has — its own count when the list was capped. */
export function positionsCount(
  positions: readonly TenderPosition[] | null | undefined,
  total: number | null | undefined,
): number {
  const n = positions?.length ?? 0;
  return typeof total === 'number' && total > n ? total : n;
}

/**
 * One entry of the /results row preview («Позиции: стол для учителя 713 шт,
 * доска меловая 324 шт … всего 24»): the name, then the quantity with its unit.
 * A nameless entry (a single-position lot, whose name IS the row's heading)
 * is just its quantity; null when it has neither.
 */
export function positionPreviewLabel(p: TenderPosition): string | null {
  const name = p.name?.trim() || null;
  const { qty } = positionFigures(p);
  if (name && qty) return `${name} ${qty}`;
  return name ?? qty;
}
